import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../app.js";
import { Api } from "../api.js";
import { mailIcon, spinnerIcon } from "../utils/icons.js";

import "../components/header-component.ts";
import "../components/footer-component.ts";

@customElement("login-page")
export class LoginPage extends BaseElement {
    @state() mail = "";
    @state() isEmailValid = false;
    @state() isMailSignedUp = false;
    @state() loading = false;
    @state() resent = false;

    validateEmail(email: string): boolean {
        const emailRegex = /^[\w+.-]+@([\w-]+\.)+[\w-]{2,}$/;
        return emailRegex.test(email);
    }

    handleInput(e: InputEvent) {
        this.mail = (e.target as HTMLInputElement).value;
        this.isEmailValid = this.validateEmail(this.mail);
    }

    async sendMagicLink() {
        if (!this.isEmailValid || this.loading) return;
        this.loading = true;
        try {
            const result = await Api.magicLink(this.mail);
            if (result instanceof Error) {
                console.error("Error creating magic link.", result.message);
                alert("Failed to send magic link. Please try again.");
                return;
            }
            this.isMailSignedUp = true;
        } catch (error) {
            console.error("Error sending magic link:", error);
            alert("Failed to send magic link. Please try again.");
        } finally {
            this.loading = false;
        }
    }

    async resendMagicLink() {
        await this.sendMagicLink();
        this.resent = true;
        setTimeout(() => (this.resent = false), 3000);
    }

    resetForm() {
        this.isMailSignedUp = false;
        this.resent = false;
    }

    private renderFormView() {
        return html`
            <div class="flex flex-col items-center gap-1">
                <img src="/logo.svg" alt="AppShovel Logo" class="w-12 h-12 mb-2" />
                <h2 class="text-2xl font-bold text-center">Sign In</h2>
                <p class="text-base-content/60 text-center">Enter your email to receive a magic login link.</p>
            </div>

            <div class="flex flex-col gap-1 mt-6">
                <label class="text-sm font-medium" for="login-email">Email</label>
                <input
                    id="login-email"
                    class="input input-bordered w-full"
                    type="email"
                    placeholder="m@example.com"
                    .value=${this.mail}
                    @input=${(e: InputEvent) => this.handleInput(e)}
                    @keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.isEmailValid && this.sendMagicLink()}
                />
            </div>

            <button
                class="btn btn-primary w-full mt-4 ${!this.isEmailValid || this.loading ? "opacity-50 cursor-not-allowed" : ""}"
                @click=${this.sendMagicLink}
                ?disabled=${!this.isEmailValid || this.loading}
            >
                ${this.loading ? html`<span class="w-5 h-5 animate-spin">${spinnerIcon}</span>` : "Continue"}
            </button>
        `;
    }

    private renderConfirmationView() {
        return html`
            <div class="flex flex-col items-center gap-4">
                <div class="rounded-full bg-green-100 p-4">
                    <div class="w-8 h-8 text-green-600">${mailIcon}</div>
                </div>

                <h2 class="text-2xl font-bold text-center">Check Your Email</h2>
                <p class="text-base-content/60 text-center">
                    We sent a magic link to <strong>${this.mail}</strong>. Click the link in the email to sign in.
                </p>

                <button
                    class="btn btn-primary w-full ${this.loading ? "opacity-50 cursor-not-allowed" : ""}"
                    @click=${this.resendMagicLink}
                    ?disabled=${this.loading}
                >
                    ${this.loading ? html`<span class="w-5 h-5 animate-spin">${spinnerIcon}</span>` : this.resent ? "Sent!" : "Resend email"}
                </button>

                <button class="btn btn-ghost btn-sm" @click=${this.resetForm}>Use a different email</button>
            </div>
        `;
    }

    render() {
        return html`
            <div class="flex flex-col h-screen container max-w-5xl p-4 md:p-6 lg:p-8 bg-base-100">
                <header-component></header-component>

                <div class="flex-grow flex items-start justify-center pt-12 md:pt-24">
                    <div class="card bg-base-100 shadow-lg border border-base-200 w-full max-w-md p-8">
                        ${this.isMailSignedUp ? this.renderConfirmationView() : this.renderFormView()}
                    </div>
                </div>

                <footer-component></footer-component>
            </div>
        `;
    }
}
